import React from "react";
import { NavLink } from "react-router-dom";
import {
  Terminal,
  LayoutDashboard,
  ShieldCheck,
  Key,
  LogOut,
  X,
  Radio,
} from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import "./Sidebar.css";

const Sidebar = ({ isOpen, onClose }) => {
  const {
    user,
    roleLabel,
    roleBadgeClass,
    canManageUsers,
    stats,
    handleLogout,
  } = useDashboard();

  const username = user.username || "Operator";
  const userInitial = username.charAt(0).toUpperCase();

  return (
    <>
      {/* Mobile Backdrop */}
      <div
        className={`bf-sidebar-backdrop ${isOpen ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Main Sidebar */}
      <aside className={`bf-sidebar ${isOpen ? "open" : ""}`}>
        {/* Brand Header */}
        <div className="bf-sidebar-header">
          <div className="bf-sidebar-brand">
            <div className="bf-sidebar-brand-icon">
              <Terminal size={20} strokeWidth={2.2} />
            </div>
            <div className="bf-sidebar-brand-text">
              <span className="bf-sidebar-title">BLACKFYRE</span>
              <span className="bf-sidebar-version">CONSOLE V2.4</span>
            </div>
          </div>

          <button
            className="bf-sidebar-close-btn"
            onClick={onClose}
            title="Close navigation menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation links */}
        <div className="bf-sidebar-nav-container">
          <div className="bf-sidebar-group">
            <span className="bf-sidebar-group-title">CORE CONSOLE</span>

            {/* Dashboard / Overview */}
            <NavLink
              to="/dashboard"
              end
              className={({ isActive }) =>
                `bf-nav-item ${isActive ? "active" : ""}`
              }
              onClick={onClose}
            >
              <div className="bf-nav-item-left">
                <span className="bf-nav-item-icon">
                  <LayoutDashboard size={18} />
                </span>
                <span>Overview</span>
              </div>
            </NavLink>

            {/* Controls (Role-gated for downline user management) */}
            {canManageUsers && (
              <NavLink
                to="/dashboard/controls"
                className={({ isActive }) =>
                  `bf-nav-item ${isActive ? "active" : ""}`
                }
                onClick={onClose}
              >
                <div className="bf-nav-item-left">
                  <span className="bf-nav-item-icon">
                    <ShieldCheck size={18} />
                  </span>
                  <span>Controls</span>
                </div>
                {stats.totalUsers > 0 && (
                  <span className="bf-nav-count-badge">{stats.totalUsers}</span>
                )}
              </NavLink>
            )}

            {/* Licenses */}
            <NavLink
              to="/dashboard/licenses"
              className={({ isActive }) =>
                `bf-nav-item ${isActive ? "active" : ""}`
              }
              onClick={onClose}
            >
              <div className="bf-nav-item-left">
                <span className="bf-nav-item-icon">
                  <Key size={18} />
                </span>
                <span>Licenses</span>
              </div>
              {stats.totalLicenses > 0 && (
                <span className="bf-nav-count-badge">
                  {stats.totalLicenses}
                </span>
              )}
            </NavLink>
          </div>

          {/* System Cluster Status Box */}
          <div className="bf-sidebar-cluster-status">
            <div className="bf-cluster-status-header">
              <span className="tech-label" style={{ fontSize: "10px" }}>
                <Radio
                  size={11}
                  className="status-dot green pulse"
                  style={{ display: "inline" }}
                />
                CLUSTER_SYNC
              </span>
              <span className="tech-badge green" style={{ fontSize: "9px" }}>
                HEALTHY
              </span>
            </div>
            <div className="bf-cluster-meta">
              <span>WORKER NODE</span>
              <span className="font-mono">AP-SOUTH1</span>
            </div>
            <div className="bf-cluster-meta">
              <span>ACTIVE KEYS</span>
              <span className="font-mono" style={{ color: "var(--green-0)" }}>
                {stats.activeCount}
              </span>
            </div>
          </div>
        </div>

        {/* Sidebar Footer with Operator profile and Disconnect button */}
        <div className="bf-sidebar-footer">
          <div className="bf-user-profile-box">
            <div className="bf-user-avatar">
              <span>{userInitial}</span>
              <span className={`status-dot ${roleBadgeClass} active`} />
            </div>
            <div className="bf-user-profile-info">
              <span className="bf-user-profile-name" title={username}>
                {username}
              </span>
              <span
                className={`tech-badge ${roleBadgeClass}`}
                style={{ fontSize: "9.5px", padding: "2px 6px" }}
              >
                {roleLabel}
              </span>
            </div>
          </div>

          <button
            className="bf-sidebar-logout-btn"
            onClick={handleLogout}
            title="Disconnect and terminate session"
          >
            <LogOut size={15} />
            <span>DISCONNECT</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
